import { ComponentProps, ReactNode, forwardRef, useRef, useState } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import Hint from '~/components/scales/Hint/Hint';
import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { StrictUnion } from '~/utilities/types';

type BaseOption = {
  disabled?: boolean;
  value: string;
};

type LabelledOption = BaseOption & {
  label: string;
  icon?: ReactNode;
  sublabel?: string;
};

type UnlabelledOption = BaseOption & {
  icon: ReactNode;
  'aria-label': string;
};

type Option = StrictUnion<LabelledOption | UnlabelledOption>;

type Props = TooltipTriggerProps & {
  // TODO DD-1188: enforce data-cy to be kebab-case
  /** provided data-cy should be kebab case and end in '-button-bar', defaults to 'button-bar' if not provided */
  'data-cy'?: string;
  defaultValue?: string;
  hint?: ComponentProps<typeof Hint>['content'];
  isDisabled?: boolean;
  isFullWidth?: boolean;
  label?: string;
  onChange: (value: string) => void;
  options: Option[];
  /** Used when using the component as a controlled component. */
  value?: string;
};

export default forwardRef<HTMLDivElement, Props>(function ButtonBar(props, forwardedRef) {
  const [controlledValue, setControlledValue] = useState(props.defaultValue);
  const value = props.value !== undefined ? props.value : controlledValue;

  const onButtonClick = (value: string) => {
    setControlledValue(value);
    props.onChange(value);
  };

  return (
    <div className={`inline-flex flex-col gap-0.5 ${props.isFullWidth ? 'w-full' : ''}`}>
      {Boolean(props.label) && (
        <div className="flex items-center gap-1">
          <div className="text-type-primary type-label">{props.label}</div>
          {Boolean(props.hint) && <Hint content={props.hint} />}
        </div>
      )}
      <div
        {...pickTooltipTriggerProps(props)}
        ref={forwardedRef}
        className="inline-flex min-h-10 rounded-md border"
        data-cy={props['data-cy'] || 'button-bar'}
      >
        {props.options.map((option) => (
          <ButtonBarButton
            {...option}
            key={option.value}
            disabled={props.isDisabled || option.disabled}
            isSelected={value === option.value}
            onClick={onButtonClick}
            width={props.isFullWidth ? `${100 / props.options.length}%` : undefined}
          />
        ))}
      </div>
    </div>
  );
});

type ButtonBarButtonProps = Option & {
  isSelected: boolean;
  onClick: (value: string) => void;
  width?: string;
};

function ButtonBarButton(props: ButtonBarButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(
    {
      isDisabled: props.disabled,
      onPress: () => props.onClick(props.value),
    },
    ref
  );

  // Manage focus via useFocusRing since focus has a habit of appearing when clicking via
  // the mouse when we rely solely on useButton.
  const { isFocusVisible, focusProps } = useFocusRing();

  let backgroundClass = 'bg-button-secondary hover:bg-button-secondary-hover';
  if (props.isSelected) {
    backgroundClass = 'bg-background-2';
  } else if (props.disabled) {
    backgroundClass = 'bg-button-secondary';
  }

  const segment = props.label ? (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      {props.icon ? (
        <span className="flex shrink-0 items-center justify-center">{props.icon}</span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-center leading-tight">
        <span className={props.sublabel ? '' : 'min-w-0 truncate'}>{props.label}</span>
        {props.sublabel ? (
          <span className="text-type-muted type-label">{props.sublabel}</span>
        ) : null}
      </div>
    </div>
  ) : (
    props.icon
  );

  return (
    <button
      {...mergeProps(buttonProps, focusProps)}
      aria-label={props['aria-label']}
      aria-pressed={props.isSelected}
      className={[
        'flex min-h-10 items-center justify-center gap-1 px-3 py-2 transition type-body1',
        'border-r last:border-0 first-of-type:rounded-s last-of-type:rounded-e',
        props.sublabel ? 'whitespace-normal' : 'truncate',
        isFocusVisible ? 'outline' : 'outline-none',
        backgroundClass,
        props.disabled ? 'cursor-not-allowed text-type-inactive' : 'text-type-primary',
      ].join(' ')}
      style={{ width: props.width }}
    >
      {segment}
    </button>
  );
}
